import { useEffect, type MutableRefObject } from "react";
import { useFetchUserProfile } from "@core/features/profile";
import { usePlatformStore } from "@platform/store";
import { type StepValidationState } from "@platform/features/onboarding";
import { User } from "@platform/types/platform.types";
import { Loader } from "@themes/pollinator/ui/Loader";
import { ProfileFormContent } from "./ProfileFormContent";

export type Props = {
  /** Optional user override (defaults to user from store) */
  user?: User | null;
  /** Optional callback when profile is saved successfully */
  onSaveSuccess?: () => void;
  /** Whether to show the cancel button (defaults to true when form is dirty) */
  showCancelButton?: boolean;
  /** Custom primary button text */
  primaryButtonText?: string;
  /** Whether to show action buttons (defaults to true) */
  showActionButtons?: boolean;
  /** Callback when step validation state changes (for parent footer) */
  onStepStateChange?: (state: StepValidationState) => void;
  /** Ref to expose the submit handler to parent */
  submitRef?: MutableRefObject<(() => Promise<void> | void) | null>;
  /** Additional CSS classes for the container */
  className?: string;
};

/**
 * Container component for ProfileForm.
 * Fetches profile data and renders ProfileFormContent only when data is ready.
 */
export function ProfileFormContainer({
  user: userProp,
  className,
  ...formProps
}: Props) {
  const storeUser = usePlatformStore((s) => s.user);
  const user = userProp !== undefined ? userProp : storeUser;
  const setUser = usePlatformStore((s) => s.setUser);

  const { data: profileResponse, isLoading: isProfileLoading } =
    useFetchUserProfile({ enabled: !!user });

  const profileData = profileResponse?.user_data;

  useEffect(() => {
    if (profileData) {
      setUser(profileData as unknown as User);
    }
  }, [profileData, setUser]);

  if (!user || isProfileLoading || !profileData) {
    return (
      <div className={className}>
        <Loader isFullPage />
      </div>
    );
  }

  return (
    <ProfileFormContent
      profileData={profileData}
      userRole={user.role || ""}
      className={className}
      {...formProps}
    />
  );
}

export default ProfileFormContainer;
